// Structured Data (Schema.org) utilities for What Grass Is This?
import { absoluteBrandAssetUrl, BRAND_ASSET_PATHS } from '@/lib/brand-assets'

export interface OrganizationSchema {
  name: string
  url: string
  description: string
  email?: string
  phone?: string
  address?: {
    country: string
    state?: string
    city?: string
  }
  logo?: string
  socialMedia?: {
    twitter?: string
    facebook?: string
    instagram?: string
    linkedin?: string
  }
}

export interface WebSiteSchema {
  name: string
  url: string
  description: string
  socialMedia?: {
    twitter?: string
    facebook?: string
    instagram?: string
    linkedin?: string
  }
}

export interface FAQSchema {
  questions: Array<{
    question: string
    answer: string
  }>
}

export interface BlogPostSchema {
  title: string
  description: string
  url: string
  author: string
  datePublished: string
  dateModified: string
  image?: string
  category: string
  tags?: string[]
  wordCount?: number
  readingTime?: number
}

export interface ProductSchema {
  name: string
  description: string
  url: string
  image?: string
  price?: string
  priceCurrency?: string
  category: string
  brand?: string
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder'
}

// Generate Organization schema
export function generateOrganizationSchema(data: OrganizationSchema) {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": data.name,
    "url": data.url,
    "description": data.description,
    "foundingDate": "2025-07",
    "knowsAbout": [
      "Lawn Care",
      "Grass Identification",
      "Lawn Maintenance",
      "Turf Management",
      "Fertilizer Application",
      "Lawn Watering",
      "Grass Types"
    ],
    "areaServed": "United States",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Lawn Care Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "AI Grass Identification",
            "description": "Identify grass types using artificial intelligence"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Personalized Lawn Care Plans",
            "description": "Custom care recommendations based on grass type and climate"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Seasonal Maintenance Guides",
            "description": "Expert advice for year-round lawn care"
          }
        }
      ]
    }
  }

  if (data.email) {
    schema.contactPoint = {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": data.email,
      "availableLanguage": "English"
    }
  }

  if (data.phone) {
    if (!schema.contactPoint) schema.contactPoint = {}
    schema.contactPoint.telephone = data.phone
  }

  if (data.address) {
    schema.address = {
      "@type": "PostalAddress",
      "addressCountry": data.address.country
    }
    if (data.address.state) schema.address.addressRegion = data.address.state
    if (data.address.city) schema.address.addressLocality = data.address.city
  }

  if (data.logo) {
    schema.logo = {
      "@type": "ImageObject",
      "url": data.logo,
      "width": 140,
      "height": 32
    }
  }

  if (data.socialMedia) {
    // Omit sameAs entirely when no profile is set: an empty array is a
    // property assertion with no value and reads as a broken entity.
    const sameAs = [
      data.socialMedia.twitter,
      data.socialMedia.facebook,
      data.socialMedia.instagram,
      data.socialMedia.linkedin,
    ].filter(Boolean)
    if (sameAs.length > 0) schema.sameAs = sameAs
  }

  return schema
}

// Generate WebSite schema
export function generateWebSiteSchema(data: WebSiteSchema) {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": data.name,
    "url": data.url,
    "description": data.description,
    "inLanguage": "en-US",
    "isAccessibleForFree": true
  }

  if (data.socialMedia) {
    // Omit sameAs entirely when no profile is set: an empty array is a
    // property assertion with no value and reads as a broken entity.
    const sameAs = [
      data.socialMedia.twitter,
      data.socialMedia.facebook,
      data.socialMedia.instagram,
      data.socialMedia.linkedin,
    ].filter(Boolean)
    if (sameAs.length > 0) schema.sameAs = sameAs
  }

  return schema
}

// Generate FAQ schema
export function generateFAQSchema(data: FAQSchema) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": data.questions.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer
      }
    }))
  }
}

// Generate BlogPost schema
export function generateBlogPostSchema(data: BlogPostSchema) {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": data.title,
    "description": data.description,
    "url": data.url,
    "author": {
      "@type": "Person",
      "name": data.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "What Grass Is This?",
      "url": "https://whatgrassisthis.com",
      "logo": {
        "@type": "ImageObject",
        "url": absoluteBrandAssetUrl(BRAND_ASSET_PATHS.iconDefault),
        "width": 512,
        "height": 512
      }
    },
    "datePublished": data.datePublished,
    "dateModified": data.dateModified,
    "articleSection": data.category,
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": data.url
    }
  }

  if (data.image) {
    schema.image = {
      "@type": "ImageObject",
      "url": data.image,
      "width": 1200,
      "height": 630
    }
  }

  if (data.tags && data.tags.length > 0) {
    schema.keywords = data.tags.join(', ')
  }

  if (data.wordCount) {
    schema.wordCount = data.wordCount
  }

  if (data.readingTime) {
    schema.timeRequired = `PT${data.readingTime}M`
  }

  return schema
}

// Generate Product schema
export function generateProductSchema(data: ProductSchema) {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": data.name,
    "description": data.description,
    "url": data.url,
    "category": data.category,
    "brand": {
      "@type": "Brand",
      "name": data.brand || "What Grass Is This?"
    },
    "offers": {
      "@type": "Offer",
      "url": data.url,
      "availability": `https://schema.org/${data.availability || 'InStock'}`,
      "priceCurrency": data.priceCurrency || "USD"
    }
  }

  if (data.price) {
    schema.offers.price = data.price
  }

  if (data.image) {
    schema.image = {
      "@type": "ImageObject",
      "url": data.image,
      "width": 800,
      "height": 600
    }
  }

  return schema
}

// Generate BreadcrumbList schema
export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  }
}

export interface HowToBlockInput {
  name: string
  description?: string
  totalTime?: string
  supplies?: string[]
  tools?: string[]
  steps: Array<{ name: string; text: string; url?: string }>
}

// Generate HowTo schema. AI-search engines (Google AI Overviews, Perplexity,
// Bing) still cite this even though Google deprecated the desktop rich result
// in 2023.
export function generateHowToSchema(block: HowToBlockInput, pageUrl: string) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: block.name,
    step: block.steps.map((s, i) => {
      const step: Record<string, unknown> = {
        "@type": "HowToStep",
        position: i + 1,
        name: s.name,
        text: s.text,
      }
      // url is optional in HowToStep; only emit it when the caller provides a
      // REAL anchor. The old `#step-N` fallback pointed at ids that don't exist
      // on the rendered page (headings get generated `toc-*` ids), and a dead
      // fragment is worse than no url per Google's HowToStep guidance.
      if (s.url) step.url = s.url
      return step
    }),
  }
  if (block.description) schema.description = block.description
  if (block.totalTime) schema.totalTime = block.totalTime
  if (block.supplies?.length) {
    schema.supply = block.supplies.map((name) => ({ "@type": "HowToSupply", name }))
  }
  if (block.tools?.length) {
    schema.tool = block.tools.map((name) => ({ "@type": "HowToTool", name }))
  }
  return schema
}

export interface LocalBusinessSchemaInput {
  name: string
  url: string
  description?: string
  telephone?: string | null
  /** The company's own site, when it has one. Distinct from `url` (our page). */
  sameAs?: string[]
  address?: {
    streetAddress?: string | null
    addressLocality: string
    addressRegion: string
    postalCode?: string | null
    addressCountry?: string
  }
  geo?: { latitude: number; longitude: number }
  /** 0 = Sunday .. 6 = Saturday. A null open/close pair means closed that day. */
  openingHours?: Array<{ day: number; open: string | null; close: string | null }>
  /** Emit ONLY for a rating we actually display, from a single named source. */
  aggregateRating?: { ratingValue: number; reviewCount: number }
  image?: string
  priceRange?: string
  areaServed?: string
}

const SCHEMA_DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

// Generate LocalBusiness schema for a directory company page.
export function generateLocalBusinessSchema(data: LocalBusinessSchemaInput) {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": data.name,
    "url": data.url,
    "@id": data.url
  }

  if (data.description) schema.description = data.description
  if (data.telephone) schema.telephone = data.telephone
  // Google Places hands back photo resource names ("places/X/photos/Y"), which
  // are not fetchable URLs. Emitting one would put a broken image in the
  // markup, so only an absolute http(s) URL is published.
  if (data.image && /^https?:\/\//i.test(data.image)) schema.image = data.image
  if (data.priceRange) schema.priceRange = data.priceRange
  if (data.areaServed) schema.areaServed = data.areaServed
  if (data.sameAs && data.sameAs.length > 0) schema.sameAs = data.sameAs

  if (data.address) {
    const address: any = {
      "@type": "PostalAddress",
      "addressLocality": data.address.addressLocality,
      "addressRegion": data.address.addressRegion,
      "addressCountry": data.address.addressCountry || "US"
    }
    if (data.address.streetAddress) address.streetAddress = data.address.streetAddress
    if (data.address.postalCode) address.postalCode = data.address.postalCode
    schema.address = address
  }

  if (data.geo && Number.isFinite(data.geo.latitude) && Number.isFinite(data.geo.longitude)) {
    schema.geo = {
      "@type": "GeoCoordinates",
      "latitude": data.geo.latitude,
      "longitude": data.geo.longitude
    }
  }

  if (data.openingHours && data.openingHours.length > 0) {
    // Closed days carry no opening hours specification at all; emitting an
    // entry with null times is invalid, and omitting the day is how
    // schema.org expresses "closed".
    const spec = data.openingHours
      .filter(h => h.open && h.close && SCHEMA_DAY_NAMES[h.day])
      .map(h => ({
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": `https://schema.org/${SCHEMA_DAY_NAMES[h.day]}`,
        "opens": h.open,
        "closes": h.close
      }))
    if (spec.length > 0) schema.openingHoursSpecification = spec
  }

  if (
    data.aggregateRating &&
    Number.isFinite(data.aggregateRating.ratingValue) &&
    data.aggregateRating.reviewCount > 0
  ) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": data.aggregateRating.ratingValue,
      "reviewCount": data.aggregateRating.reviewCount,
      "bestRating": 5,
      "worstRating": 1
    }
  }

  return schema
}

export interface ItemListSchemaInput {
  name: string
  /** Page the list lives on. Used for the `@id` so multiple lists stay distinct. */
  url: string
  anchor?: string
  items: Array<{ name: string; url: string }>
  description?: string
}

// Generate ItemList schema for a directory listing (states, cities, companies).
export function generateItemListSchema(data: ItemListSchemaInput) {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${data.url}#${data.anchor || 'list'}`,
    "name": data.name,
    "numberOfItems": data.items.length,
    "itemListOrder": "https://schema.org/ItemListOrderAscending",
    "itemListElement": data.items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "url": item.url
    }))
  }
  if (data.description) schema.description = data.description
  return schema
}

export interface CollectionPageSchemaInput {
  name: string
  url: string
  description?: string
  /** The place the collection is scoped to, when it is a geographic facet. */
  about?: { name: string; latitude?: number; longitude?: number }
  numberOfItems?: number
}

/**
 * CollectionPage for a directory list surface (state hub, service facet).
 * The companies themselves go in a sibling ItemList; keeping the two separate
 * lets a page carry the list under its own @id without nesting two graphs.
 */
export function generateCollectionPageSchema(data: CollectionPageSchemaInput) {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": data.url,
    "name": data.name,
    "url": data.url,
    "isPartOf": {
      "@type": "WebSite",
      "name": "What Grass Is This?",
      "url": "https://whatgrassisthis.com"
    }
  }
  if (data.description) schema.description = data.description
  if (typeof data.numberOfItems === 'number') schema.numberOfItems = data.numberOfItems
  if (data.about) {
    const place: any = { "@type": "Place", "name": data.about.name }
    if (typeof data.about.latitude === 'number' && typeof data.about.longitude === 'number') {
      place.geo = {
        "@type": "GeoCoordinates",
        "latitude": data.about.latitude,
        "longitude": data.about.longitude
      }
    }
    schema.about = place
  }
  return schema
}

// ─────────────────────────────────────────────────────────────────────────────
// Attributed content images (ImageObject nodes)
// ─────────────────────────────────────────────────────────────────────────────

const SITE_ORIGIN = 'https://whatgrassisthis.com'

/**
 * Maps the human-readable CC license labels our photo records carry to the
 * canonical license URIs Google's image-license documentation expects. A label
 * that is not in this map yields NO license fields at all: an ImageObject that
 * claims a Licensable badge without a resolvable license record is a false
 * claim, and Google penalises the mismatch rather than ignoring it.
 *
 * Lives here (rather than in blog-post-page-utils, which re-exports it for its
 * existing importers) so compare/weeds/pest pages can build license-bearing
 * ImageObjects without pulling in the blog HTML parser.
 */
export const CC_LICENSE_URLS: Record<string, string> = {
  'CC BY-SA 4.0': 'https://creativecommons.org/licenses/by-sa/4.0/',
  'CC BY-SA 3.0': 'https://creativecommons.org/licenses/by-sa/3.0/',
  'CC BY-SA 2.0': 'https://creativecommons.org/licenses/by-sa/2.0/',
  'CC BY 4.0': 'https://creativecommons.org/licenses/by/4.0/',
  'CC BY 3.0': 'https://creativecommons.org/licenses/by/3.0/',
  'CC BY 2.0': 'https://creativecommons.org/licenses/by/2.0/',
  'CC0': 'https://creativecommons.org/publicdomain/zero/1.0/',
  'Public domain': 'https://creativecommons.org/publicdomain/mark/1.0/',
}

/**
 * The attribution shape every curated photo record on the site already has:
 * ReferencePhoto / ReferenceImage (data/grass-reference-photos), WeedPagePhoto,
 * PestPagePhoto, and the Directus `content_image_meta` entries.
 */
export interface AttributedPhotoRecord {
  src: string
  alt?: string | null
  caption?: string | null
  credit?: string | null
  license?: string | null
  sourceUrl?: string | null
}

export interface AttributedImageObject {
  '@type': 'ImageObject'
  contentUrl: string
  name?: string
  description?: string
  caption?: string
  creditText?: string
  license?: string
  acquireLicensePage?: string
}

/** Root-relative /public paths need an origin before they can be a contentUrl. */
export function absoluteContentUrl(src: string, origin: string = SITE_ORIGIN): string {
  if (!src) return src
  return src.startsWith('/') ? `${origin}${src}` : src
}

/**
 * One ImageObject node for a curated, attributed photo. `name` and `caption`
 * are page-specific (a guide says "Bermuda grass: seed head", a weed page says
 * "Crabgrass in a lawn"), so the caller supplies them; everything else travels
 * with the photo record.
 *
 * License/acquireLicensePage are emitted ONLY when the record's license label
 * resolves in CC_LICENSE_URLS, so a photo with no license record never carries
 * a Licensable claim.
 */
export function buildAttributedImageObject(
  photo: AttributedPhotoRecord | null | undefined,
  options: {
    name?: string
    caption?: string
    description?: string
    origin?: string
  } = {},
): AttributedImageObject | null {
  if (!photo?.src) return null

  const licenseUrl = photo.license ? CC_LICENSE_URLS[photo.license] : undefined
  const name = options.name ?? photo.alt ?? undefined
  const caption = options.caption ?? photo.caption ?? undefined
  const description = options.description ?? photo.alt ?? undefined

  const node: AttributedImageObject = {
    '@type': 'ImageObject',
    contentUrl: absoluteContentUrl(photo.src, options.origin),
  }
  if (name) node.name = name
  if (description && description !== name) node.description = description
  if (caption) node.caption = caption
  if (photo.credit) node.creditText = photo.credit
  if (licenseUrl) {
    node.license = licenseUrl
    if (photo.sourceUrl) node.acquireLicensePage = photo.sourceUrl
  }
  return node
}

/** Same rules, applied across a photo list; unusable records drop out. */
export function buildAttributedImageObjects(
  photos: ReadonlyArray<AttributedPhotoRecord | null | undefined>,
  naming: (
    photo: AttributedPhotoRecord,
    index: number,
  ) => { name?: string; caption?: string; description?: string },
  origin?: string,
): AttributedImageObject[] {
  const nodes: AttributedImageObject[] = []
  photos.forEach((photo, index) => {
    if (!photo?.src) return
    const node = buildAttributedImageObject(photo, { ...naming(photo, index), origin })
    if (node) nodes.push(node)
  })
  return nodes
}
